import { HelpdeskDb } from 'lib/cosmos/helpdesk/helpdeskDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { linkedItemsHelper } from 'lib/helpers/linked-items-helper.class.js';
import { unitConversionHelper } from 'lib/helpers/unit-conversion-helper.class.js';
import { TagListItem } from 'lib/ui/tag/tag-list.component.js';
import { map, of } from 'rxjs';
import { CoreAppComponentDependencies } from 'src/app/core/core-app-dependencies.js';
import { environment } from 'src/app/environments/environment.js';
import { HelpdeskConversationComponent } from 'src/app/modules/chat/helpdesk-conversation.component.js';
import { HelpdeskListComponent } from 'src/app/modules/chat/helpdesk-list.component.js';
import {
	ICoreRoutes,
	IMetadataContext,
	IPageMetadata,
	ISharedData,
	ITypePageCard,
	ITypeRoute,
	ITypeRouteAction,
	RouteInitializer
} from '../../pages/type-routes/type-route.models.js';

export interface IHelpdeskRoutes extends ICoreRoutes {
	view: ITypeRoute<
		undefined,
		{
			conversations: ITypeRouteAction<undefined>;
		}
	>;
	conversation: ITypeRoute<
		HelpdeskDb.IHelpdeskItem,
		{
			form: ITypeRouteAction<HelpdeskDb.IHelpdeskItem>;
		}
	>;
}

export const helpdeskRoutes: RouteInitializer<IHelpdeskRoutes> = {
	view: {
		mainPath: 'helpdesk',
		uniqueId: 'helpdesAdminkOverview',
		parentRoutes: (context) => [],
		loadSharedData: (context) => {
			return of(undefined);
		},
		menuItems: (context) => [],
		children: {
			conversations: {
				uniqueId: `helpdeskConversations`,
				component: HelpdeskListComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: 'Conversations'
					};
					return metadata;
				}
			}
		}
	},
	conversation: {
		mainPath: 'helpdesk',
		uniqueId: 'helpdeskConversationOverview',
		parentRoutes: (context) => {
			if (context.user?.privilege === 'admin') {
				return [
					{
						title: 'Conversations',
						routerLink: context.dependencies.navigateService
							.actionRoute((routes) => routes.helpdeskRoutes.view.children.conversations)
							.getUrl()
					}
				];
			}

			return [];
		},
		loadSharedData: (context) => {
			return context.dependencies.httpService
				.get<HelpdeskDb.IHelpdeskItem>((builder) =>
					builder
						.withCustomApiRoute('helpdesk', 'getHelpdeskData')
						.withId(context.routeData.primaryId)
						.build()
				)
				.pipe(
					map((response) => {
						const sharedData: ISharedData<HelpdeskDb.IHelpdeskItem> = {
							data: response.data,
							linkedItems: response.linkedItems
						};

						return sharedData;
					})
				);
		},
		menuItems: (context) => [],
		children: {
			form: {
				uniqueId: `helpdeskConversation`,
				component: HelpdeskConversationComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: 'Helpdesk',
						card: getCard(data, data.dependencies)
					};
					return metadata;
				}
			}
		}
	}
};

function getCard(
	data: IMetadataContext<HelpdeskDb.IHelpdeskItem>,
	dependencies: CoreAppComponentDependencies
): ITypePageCard | undefined {
	const user = linkedItemsHelper.getLinkedItemById<UserDb.IUserItem>(
		data.sharedData?.linkedItems ?? [],
		data.sharedData?.data.userId ?? ''
	);

	if (!user) {
		return undefined;
	}

	const authUser = data.dependencies.authService.authUser();
	const appContext = data.dependencies.appDataService.appContext();

	if (authUser?.privilege === 'default') {
		return {
			title: { value: `Ask question` },
			subtitle: `We'll get back to you as soon as we can. Usually within a day.`,
			avatar: {
				avatar: appContext?.appSetup.helpdeskImage?.[0]?.image,
				avatarName: environment.appName
			}
		};
	}

	const tags: TagListItem[] = [];

	if (!dependencies.screenService.currentScreen().isMobileView) {
		tags.push({
			icon: 'email',
			text: user.email
		});
	}

	if (user.preferredUnit) {
		if (user.heightCm) {
			tags.push({
				icon: 'height',
				text: unitConversionHelper.heightInPreferredUnit(user?.preferredUnit, user.heightCm)
			});
		}

		if (user.weightKg) {
			tags.push({
				icon: 'weight',
				text: unitConversionHelper.weightInPreferredUnit(user?.preferredUnit, user.weightKg)
			});
		}
	}

	if (user.timezone) {
		tags.push({
			icon: 'timezone',
			text: `${user.timezone}`
		});
	}

	return {
		title: { value: user.system.name },
		avatar: {
			avatar: user.avatar,
			avatarName: user.system.name
		},
		tags: tags,
		subtitle: user.lastActiveDate
			? `Last active ${data.dependencies.timeService.fromNow(user.lastActiveDate)}`
			: 'Not active yet'
	};
}
